import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { usePermissions } from '../hooks/usePermissions';
import { addChild, updateChild } from '@coparent/shared/src/services/childService';
import { updateFamilySettings, updateParentProfileField, addPlaceholderParent, removePlaceholderParent, addHouseholdMember, removeHouseholdMember, generateHouseholdInvite, generateCoparentInvite, generateMergeInvite } from '@coparent/shared/src/services/familyService';
import { DEFAULT_CHILD_COLORS } from '@coparent/shared/src/utils/constants';
import { HiUserGroup, HiUsers, HiCog, HiClipboard, HiColorSwatch, HiCalendar, HiUserAdd, HiX, HiHome, HiLink, HiPencil, HiCheck, HiTrash, HiLogout, HiDeviceMobile, HiChevronRight } from 'react-icons/hi';
import { Link, useNavigate } from 'react-router-dom';
import GoogleCalendarSettings from '../components/settings/GoogleCalendarSettings';
import toast from 'react-hot-toast';
import NotificationBell from '../components/notifications/NotificationBell';
import NotificationSettings from '../components/settings/NotificationSettings';
import FamilyTimezoneSettings from '../components/settings/FamilyTimezoneSettings';

const COLOR_OPTIONS = [
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#10b981', name: 'Emerald' },
  { hex: '#06b6d4', name: 'Cyan' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#84cc16', name: 'Lime' },
];

const SettingsPage = () => {
  const { currentUser, logout } = useAuth();
  const { family, familyId, familyChildren, childrenMap, getFamilyDisplayName, getHouseholdMembers } = useFamily();
  const { canManageFamilySettings, canRemoveMembers } = usePermissions();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };
  const [showAddChild, setShowAddChild] = useState(false);
  const [childName, setChildName] = useState('');
  const [savingColor, setSavingColor] = useState(null);
  const [savingLabels, setSavingLabels] = useState(false);
  const [editingFamilyName, setEditingFamilyName] = useState(false);
  const [customFamilyName, setCustomFamilyName] = useState('');
  const [savingFamilyName, setSavingFamilyName] = useState(false);
  const [showAddParent, setShowAddParent] = useState(false);
  const [newParentName, setNewParentName] = useState('');
  const [addingParent, setAddingParent] = useState(false);

  // Parent labels state
  const parentA = family?.parentIds?.[0];
  const parentB = family?.parentIds?.[1];
  const [labelA, setLabelA] = useState('');
  const [labelB, setLabelB] = useState('');
  const [labelsInitialized, setLabelsInitialized] = useState(false);

  // Initialize labels from family settings
  if (family && !labelsInitialized) {
    setLabelA(family.settings?.parentLabels?.[parentA] || '');
    setLabelB(family.settings?.parentLabels?.[parentB] || '');
    setLabelsInitialized(true);
  }

  const handleAddChild = async (e) => {
    e.preventDefault();
    if (!childName.trim()) return;
    try {
      await addChild(familyId, {
        firstName: childName,
        dateOfBirth: null,
        school: null,
        grade: null,
        medicalNotes: null,
        emergencyContacts: [],
        photoURL: null
      }, familyChildren.length);
      setChildName('');
      setShowAddChild(false);
      toast.success('Child added');
    } catch (error) {
      toast.error('Failed to add child');
    }
  };

  const copyInviteCode = () => {
    if (family?.inviteCode) {
      navigator.clipboard.writeText(family.inviteCode);
      toast.success('Invite code copied!');
    }
  };

  const handleSaveFamilyName = async () => {
    setSavingFamilyName(true);
    try {
      const value = customFamilyName.trim() || null;
      await updateParentProfileField(familyId, currentUser.uid, 'familyName', value);
      toast.success('Family name updated');
      setEditingFamilyName(false);
    } catch (error) {
      toast.error('Failed to update family name');
    }
    setSavingFamilyName(false);
  };

  const handleColorChange = async (parentId, color) => {
    setSavingColor(parentId);
    try {
      await updateParentProfileField(familyId, parentId, 'color', color);
      toast.success('Color updated');
    } catch (error) {
      toast.error('Failed to update color');
    }
    setSavingColor(null);
  };

  const handleSaveLabels = async () => {
    setSavingLabels(true);
    try {
      const parentLabels = {};
      if (parentA) parentLabels[parentA] = labelA.trim();
      if (parentB) parentLabels[parentB] = labelB.trim();
      await updateFamilySettings(familyId, { parentLabels });
      toast.success('Parent labels updated');
    } catch (error) {
      toast.error('Failed to update labels');
    }
    setSavingLabels(false);
  };

  const handleAddPlaceholderParent = async (e) => {
    e.preventDefault();
    if (!newParentName.trim()) return;
    setAddingParent(true);
    try {
      await addPlaceholderParent(familyId, newParentName.trim());
      setNewParentName('');
      setShowAddParent(false);
      toast.success('Co-parent added');
    } catch (error) {
      toast.error('Failed to add co-parent');
    }
    setAddingParent(false);
  };

  const handleRemovePlaceholder = async (pid) => {
    try {
      await removePlaceholderParent(familyId, pid);
      toast.success('Co-parent removed');
    } catch (error) {
      toast.error('Failed to remove co-parent');
    }
  };

  // Household member state
  const [showAddHouseholdMember, setShowAddHouseholdMember] = useState(null); // parentId or null
  const [hmName, setHmName] = useState('');
  const [hmColor, setHmColor] = useState('#8b5cf6');
  const [addingHm, setAddingHm] = useState(false);
  const [householdInviteCode, setHouseholdInviteCode] = useState(null);
  const [generatingInvite, setGeneratingInvite] = useState(null);
  const [mergeInviteCodes, setMergeInviteCodes] = useState({});
  const [generatingMergeInvite, setGeneratingMergeInvite] = useState(null);
  const [generatingCoparentInvite, setGeneratingCoparentInvite] = useState(false);

  const buildInviteUrl = (code) => `${window.location.origin}/invite/${code}`;

  const handleAddHouseholdMember = async (e, parentId) => {
    e.preventDefault();
    if (!hmName.trim()) return;
    setAddingHm(true);
    try {
      await addHouseholdMember(familyId, {
        displayName: hmName.trim(),
        color: hmColor,
        householdOf: parentId,
        isRegistered: false
      });
      setHmName('');
      setHmColor('#8b5cf6');
      setShowAddHouseholdMember(null);
      toast.success('Household member added');
    } catch (error) {
      toast.error('Failed to add household member');
    }
    setAddingHm(false);
  };

  const handleRemoveHouseholdMember = async (memberId) => {
    try {
      await removeHouseholdMember(familyId, memberId);
      toast.success('Household member removed');
    } catch (error) {
      toast.error('Failed to remove household member');
    }
  };

  const handleGenerateHouseholdInvite = async (parentId) => {
    setGeneratingInvite(parentId);
    try {
      const code = await generateHouseholdInvite(familyId, parentId);
      setHouseholdInviteCode({ parentId, code });
      navigator.clipboard.writeText(buildInviteUrl(code));
      toast.success('Invite link copied!');
    } catch (error) {
      toast.error('Failed to generate invite');
    }
    setGeneratingInvite(null);
  };

  const handleGenerateCoparentInvite = async () => {
    setGeneratingCoparentInvite(true);
    try {
      const code = await generateCoparentInvite(familyId, currentUser.uid);
      const url = buildInviteUrl(code);
      navigator.clipboard.writeText(url);
      toast.success('Invite link copied!');
    } catch (error) {
      toast.error('Failed to generate invite link');
    }
    setGeneratingCoparentInvite(false);
  };

  const handleGenerateMergeInvite = async (targetId, targetType) => {
    setGeneratingMergeInvite(targetId);
    try {
      const code = await generateMergeInvite(familyId, currentUser.uid, targetId, targetType);
      setMergeInviteCodes(prev => ({ ...prev, [targetId]: code }));
      const url = buildInviteUrl(code);
      navigator.clipboard.writeText(url);
      toast.success('Invite link copied!');
    } catch (error) {
      toast.error(error.message || 'Failed to generate invite link');
    }
    setGeneratingMergeInvite(null);
  };

  if (!family) return null;

  const getParentDisplayName = (pid) => {
    const label = family.settings?.parentLabels?.[pid];
    if (label) return label;
    return family.parentProfiles[pid]?.displayName || 'Parent';
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title">Settings</h1>
        </div>
        <NotificationBell />
      </div>

      {/* Family Info */}
      <div className="card p-6 mb-4">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-4">
          <div className="w-7 h-7 rounded-lg bg-parent1-50 flex items-center justify-center">
            <HiUserGroup className="text-parent1-500 text-sm" />
          </div>
          Family
        </h2>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide">Family Name</p>
            {editingFamilyName ? (
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  value={customFamilyName}
                  onChange={e => setCustomFamilyName(e.target.value)}
                  className="input-base text-sm flex-1"
                  placeholder={family.name}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveFamilyName();
                    if (e.key === 'Escape') setEditingFamilyName(false);
                  }}
                />
                <button
                  onClick={handleSaveFamilyName}
                  disabled={savingFamilyName}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-canopy hover:bg-canopy/10 transition-colors"
                >
                  <HiCheck className="text-base" />
                </button>
                <button
                  onClick={() => setEditingFamilyName(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 transition-colors"
                >
                  <HiX className="text-base" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-0.5">
                <p className="font-semibold text-ink-900">{getFamilyDisplayName()}</p>
                <button
                  onClick={() => {
                    const profile = family.parentProfiles?.[currentUser.uid];
                    setCustomFamilyName(profile?.familyName || '');
                    setEditingFamilyName(true);
                  }}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-ink-300 hover:text-ink-500 hover:bg-surface-100 transition-colors"
                >
                  <HiPencil className="text-xs" />
                </button>
              </div>
            )}
            <p className="text-[0.6875rem] text-ink-300 mt-1">Only you see this name</p>
          </div>
          {family.parentIds.filter(pid => !family.parentProfiles[pid]?.placeholder).length < 2 && (
            <div>
              <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide">Co-Parent Invite Link</p>
              {family.inviteCode ? (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-accent-600 font-medium truncate flex-1">{buildInviteUrl(family.inviteCode)}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(buildInviteUrl(family.inviteCode));
                        toast.success('Invite link copied!');
                      }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors flex-shrink-0"
                    >
                      <HiClipboard className="text-base" />
                    </button>
                  </div>
                  <p className="text-xs text-ink-300 mt-0.5">Share this link with your co-parent to join</p>
                </div>
              ) : null}
              <button
                onClick={handleGenerateCoparentInvite}
                disabled={generatingCoparentInvite}
                className="text-xs text-accent-600 hover:text-accent-700 font-semibold transition-colors flex items-center gap-1 mt-2"
              >
                <HiLink className="text-sm" />
                {generatingCoparentInvite ? 'Generating...' : 'Generate New Invite Link'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Parents */}
      <div className="card p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem]">
            <div className="w-7 h-7 rounded-lg bg-parent2-50 flex items-center justify-center">
              <HiUsers className="text-parent2-500 text-sm" />
            </div>
            Parents
          </h2>
          {canManageFamilySettings && family.parentIds.length < 2 && !showAddParent && (
            <button
              onClick={() => setShowAddParent(true)}
              className="text-sm text-accent-600 hover:text-accent-700 font-semibold transition-colors flex items-center gap-1"
            >
              <HiUserAdd className="text-base" />
              Add Co-Parent
            </button>
          )}
        </div>
        <div className="space-y-3">
          {family.parentIds.map(pid => {
            const profile = family.parentProfiles[pid];
            const isPlaceholder = profile?.placeholder;
            return (
              <div key={pid} className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ring-2 ring-white shadow-warm-sm"
                  style={{ backgroundColor: profile?.color }}
                >
                  {profile?.displayName?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-ink-900 text-sm">
                    {profile?.displayName}
                    {pid === currentUser.uid && <span className="text-xs text-ink-300 ml-2 font-medium">(you)</span>}
                    {isPlaceholder && (
                      <span className="text-xs text-amber-500 ml-2 font-medium bg-amber-50 px-1.5 py-0.5 rounded-full">
                        not registered
                      </span>
                    )}
                  </p>
                </div>
                {isPlaceholder && canManageFamilySettings && (
                  <>
                    <button
                      onClick={() => handleGenerateMergeInvite(pid, 'parent')}
                      disabled={generatingMergeInvite === pid}
                      className="text-xs text-accent-600 hover:text-accent-700 font-semibold transition-colors flex items-center gap-1"
                      title="Generate invite link for this person"
                    >
                      <HiLink className="text-sm" />
                      {generatingMergeInvite === pid ? '...' : 'Invite'}
                    </button>
                    <button
                      onClick={() => handleRemovePlaceholder(pid)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                      title="Remove co-parent"
                    >
                      <HiX className="text-base" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {showAddParent && (
          <form onSubmit={handleAddPlaceholderParent} className="mt-4">
            <p className="text-xs text-ink-400 mb-2">
              Add your co-parent by name. They don't need an account yet — you can set up the calendar now and they can join later with the invite code.
            </p>
            <div className="flex gap-2">
              <input
                value={newParentName}
                onChange={e => setNewParentName(e.target.value)}
                type="text"
                className="input-base flex-1 !py-2 text-sm"
                placeholder="Co-parent's name"
                autoFocus
              />
              <button type="submit" disabled={addingParent} className="btn-primary !py-2 text-sm">
                {addingParent ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => { setShowAddParent(false); setNewParentName(''); }}
                className="px-3 py-2 text-ink-400 text-sm font-medium hover:text-ink-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        {family.parentIds.some(pid => family.parentProfiles[pid]?.placeholder) && (
          <div className="mt-4 space-y-2">
            {family.parentIds.filter(pid => family.parentProfiles[pid]?.placeholder).map(pid => (
              mergeInviteCodes[pid] && (
                <div key={pid} className="p-3 bg-surface-50 rounded-xl border border-surface-200">
                  <p className="text-xs text-ink-400">
                    Invite link for <span className="font-semibold text-ink-600">{family.parentProfiles[pid]?.displayName}</span>:
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-accent-600 font-medium truncate flex-1">{buildInviteUrl(mergeInviteCodes[pid])}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(buildInviteUrl(mergeInviteCodes[pid]));
                        toast.success('Invite link copied!');
                      }}
                      className="w-6 h-6 rounded flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors flex-shrink-0"
                    >
                      <HiClipboard className="text-xs" />
                    </button>
                  </div>
                </div>
              )
            ))}
            <p className="text-xs text-ink-300 px-1">
              Click "Invite" next to a placeholder to generate a personal invite link. When they sign up, they'll automatically replace the placeholder.
            </p>
          </div>
        )}
      </div>

      {/* Household Members */}
      <div className="card p-6 mb-4">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-4">
          <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center">
            <HiHome className="text-purple-500 text-sm" />
          </div>
          Household Members
        </h2>
        <p className="text-xs text-ink-400 mb-4">Add step-parents or partners to each household</p>
        <div className="space-y-5">
          {family.parentIds.map(pid => {
            const profile = family.parentProfiles[pid];
            const members = getHouseholdMembers(pid);
            return (
              <div key={pid}>
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: profile?.color }}
                  >
                    {profile?.displayName?.[0]?.toUpperCase()}
                  </div>
                  <p className="text-sm font-semibold text-ink-700">
                    {getParentDisplayName(pid)}'s Household
                  </p>
                </div>
                <div className="ml-8 space-y-2">
                  {members.map(member => (
                    <div key={member.id} className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: member.color }}
                      >
                        {member.displayName?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-ink-700 font-medium">
                          {member.displayName}
                          {member.id === currentUser.uid && (
                            <span className="text-xs text-ink-300 ml-1.5 font-medium">(you)</span>
                          )}
                          {!member.isRegistered && (
                            <span className="text-xs text-amber-500 ml-1.5 font-medium bg-amber-50 px-1.5 py-0.5 rounded-full">
                              not registered
                            </span>
                          )}
                        </p>
                      </div>
                      {!member.isRegistered && (
                        <button
                          onClick={() => handleGenerateMergeInvite(member.id, 'household_member')}
                          disabled={generatingMergeInvite === member.id}
                          className="text-xs text-accent-600 hover:text-accent-700 font-semibold transition-colors flex items-center gap-1"
                          title="Generate invite link for this person"
                        >
                          <HiLink className="text-xs" />
                          {generatingMergeInvite === member.id ? '...' : 'Invite'}
                        </button>
                      )}
                      {canRemoveMembers && (
                        <button
                          onClick={() => handleRemoveHouseholdMember(member.id)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                          title="Remove member"
                        >
                          <HiX className="text-sm" />
                        </button>
                      )}
                    </div>
                  ))}
                  {members.length === 0 && (
                    <p className="text-xs text-ink-300 italic">No household members yet</p>
                  )}
                  {showAddHouseholdMember === pid ? (
                    <form onSubmit={(e) => handleAddHouseholdMember(e, pid)} className="mt-2">
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <input
                            value={hmName}
                            onChange={e => setHmName(e.target.value)}
                            type="text"
                            className="input-base !py-1.5 text-sm"
                            placeholder="Name"
                            autoFocus
                          />
                        </div>
                        <div className="flex gap-1">
                          {COLOR_OPTIONS.slice(0, 5).map(({ hex }) => (
                            <button
                              key={hex}
                              type="button"
                              onClick={() => setHmColor(hex)}
                              className={`w-6 h-6 rounded-full transition-all ${
                                hmColor === hex ? 'ring-2 ring-offset-1 ring-ink-400 scale-110' : ''
                              }`}
                              style={{ backgroundColor: hex }}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button type="submit" disabled={addingHm} className="btn-primary !py-1.5 text-xs">
                          {addingHm ? 'Adding...' : 'Add'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowAddHouseholdMember(null); setHmName(''); }}
                          className="px-2 py-1.5 text-ink-400 text-xs font-medium hover:text-ink-600 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    canManageFamilySettings && (
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => setShowAddHouseholdMember(pid)}
                          className="text-xs text-accent-600 hover:text-accent-700 font-semibold transition-colors flex items-center gap-1"
                        >
                          <HiUserAdd className="text-sm" />
                          Add Member
                        </button>
                        <button
                          onClick={() => handleGenerateHouseholdInvite(pid)}
                          disabled={generatingInvite === pid}
                          className="text-xs text-ink-400 hover:text-ink-600 font-semibold transition-colors flex items-center gap-1"
                        >
                          <HiClipboard className="text-sm" />
                          {generatingInvite === pid ? 'Generating...' : 'Invite Link'}
                        </button>
                      </div>
                    )
                  )}
                  {householdInviteCode?.parentId === pid && (
                    <div className="mt-2 p-2.5 bg-surface-50 rounded-lg border border-surface-200">
                      <p className="text-xs text-ink-400 mb-1">Share this link to join as a household member:</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-accent-600 font-medium truncate flex-1">{buildInviteUrl(householdInviteCode.code)}</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(buildInviteUrl(householdInviteCode.code));
                            toast.success('Invite link copied!');
                          }}
                          className="w-6 h-6 rounded flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors flex-shrink-0"
                        >
                          <HiClipboard className="text-xs" />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Merge invite links for unregistered household members */}
                  {members.filter(m => !m.isRegistered && mergeInviteCodes[m.id]).map(m => (
                    <div key={m.id} className="mt-2 p-2.5 bg-surface-50 rounded-lg border border-surface-200">
                      <p className="text-xs text-ink-400 mb-1">
                        Invite link for <span className="font-semibold text-ink-600">{m.displayName}</span>:
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-accent-600 font-medium truncate flex-1">{buildInviteUrl(mergeInviteCodes[m.id])}</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(buildInviteUrl(mergeInviteCodes[m.id]));
                            toast.success('Invite link copied!');
                          }}
                          className="w-6 h-6 rounded flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors flex-shrink-0"
                        >
                          <HiClipboard className="text-xs" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Parent Labels */}
      {canManageFamilySettings && <div className="card p-6 mb-4">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-4">
          <div className="w-7 h-7 rounded-lg bg-accent-50 flex items-center justify-center">
            <HiUsers className="text-accent-500 text-sm" />
          </div>
          Parent Labels
        </h2>
        <p className="text-xs text-ink-400 mb-4">Custom names shown on the calendar (e.g. "Mom", "Dad")</p>
        <div className="space-y-3">
          {parentA && (
            <div>
              <label className="text-xs text-ink-400 font-semibold uppercase tracking-wide">
                Parent A ({family.parentProfiles[parentA]?.displayName})
              </label>
              <input
                type="text"
                value={labelA}
                onChange={e => setLabelA(e.target.value)}
                className="input-base mt-1 text-sm"
                placeholder="e.g. Mom, Dad, Parent A..."
              />
            </div>
          )}
          {parentB && (
            <div>
              <label className="text-xs text-ink-400 font-semibold uppercase tracking-wide">
                Parent B ({family.parentProfiles[parentB]?.displayName})
              </label>
              <input
                type="text"
                value={labelB}
                onChange={e => setLabelB(e.target.value)}
                className="input-base mt-1 text-sm"
                placeholder="e.g. Mom, Dad, Parent B..."
              />
            </div>
          )}
          <button
            onClick={handleSaveLabels}
            disabled={savingLabels}
            className="btn-primary !py-2 text-sm mt-2"
          >
            {savingLabels ? 'Saving...' : 'Save Labels'}
          </button>
        </div>
      </div>}

      {/* Parent Colors */}
      {canManageFamilySettings && <div className="card p-6 mb-4">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-4">
          <div className="w-7 h-7 rounded-lg bg-shared-50 flex items-center justify-center">
            <HiColorSwatch className="text-shared-500 text-sm" />
          </div>
          Calendar Colors
        </h2>
        <p className="text-xs text-ink-400 mb-4">Choose a color to represent each parent on the calendar</p>
        <div className="space-y-5">
          {family.parentIds.map((pid, idx) => {
            const profile = family.parentProfiles[pid];
            const currentColor = profile?.color;
            return (
              <div key={pid}>
                <p className="text-sm font-semibold text-ink-700 mb-2">
                  {getParentDisplayName(pid)}
                  {pid === currentUser.uid && <span className="text-xs text-ink-300 ml-2 font-medium">(you)</span>}
                </p>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map(({ hex, name }) => (
                    <button
                      key={hex}
                      onClick={() => handleColorChange(pid, hex)}
                      disabled={savingColor === pid}
                      title={name}
                      className={`w-9 h-9 rounded-full transition-all duration-150 ${
                        currentColor === hex
                          ? 'ring-2 ring-offset-2 ring-ink-400 scale-110'
                          : 'hover:scale-110 hover:ring-2 hover:ring-offset-1 hover:ring-ink-200'
                      }`}
                      style={{ backgroundColor: hex }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>}

      {/* Custody Schedule */}
      {canManageFamilySettings && (
        <Link
          to="/custody/setup"
          className="card p-6 mb-4 flex items-center gap-4 hover:border-surface-300 transition-colors group"
        >
          <div className="w-10 h-10 rounded-lg bg-parent1-50 flex items-center justify-center flex-shrink-0">
            <HiCalendar className="text-parent1-500 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-ink-900 text-[0.9375rem]">Custody Schedule</h2>
            <p className="text-xs text-ink-400 mt-0.5">Choose or customize your custody rotation pattern</p>
          </div>
          <HiChevronRight className="text-ink-300 text-xl flex-shrink-0 group-hover:text-ink-500 transition-colors" />
        </Link>
      )}

      {/* Google Calendar Sync */}
      <GoogleCalendarSettings />

      {/* Children */}
      <div className="card p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-ink-900 text-[0.9375rem]">Children</h2>
          {canManageFamilySettings && (
            <button
              onClick={() => setShowAddChild(true)}
              className="text-sm text-accent-600 hover:text-accent-700 font-semibold transition-colors"
            >
              + Add Child
            </button>
          )}
        </div>
        <div className="space-y-3">
          {familyChildren.map(child => {
            const color = childrenMap[child.id]?.color || '#8b5cf6';
            return (
              <div key={child.id} className="py-2">
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: color }}
                  >
                    {child.firstName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink-900 text-sm">{child.firstName}</p>
                    {child.school && <p className="text-xs text-ink-400">{child.school}</p>}
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2 ml-12">
                  {DEFAULT_CHILD_COLORS.map(c => (
                    <button
                      key={c.hex}
                      type="button"
                      onClick={async () => {
                        try {
                          await updateChild(familyId, child.id, { color: c.hex });
                        } catch {
                          toast.error('Failed to update color');
                        }
                      }}
                      className="w-6 h-6 rounded-full transition-all duration-150 flex items-center justify-center"
                      style={{
                        backgroundColor: c.hex,
                        boxShadow: color === c.hex ? `0 0 0 2px #fff, 0 0 0 3px ${c.hex}` : 'none',
                        transform: color === c.hex ? 'scale(1.15)' : 'scale(1)',
                      }}
                      title={c.name}
                    >
                      {color === c.hex && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {showAddChild && (
          <form onSubmit={handleAddChild} className="mt-4 flex gap-2">
            <input
              value={childName}
              onChange={e => setChildName(e.target.value)}
              type="text"
              className="input-base flex-1 !py-2 text-sm"
              placeholder="Child's name"
              autoFocus
            />
            <button type="submit" className="btn-primary !py-2 text-sm">
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowAddChild(false)}
              className="px-3 py-2 text-ink-400 text-sm font-medium hover:text-ink-600 transition-colors"
            >
              Cancel
            </button>
          </form>
        )}
      </div>

      {/* Default Split */}
      <div className="card p-6 mb-6">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-4">
          <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
            <HiCog className="text-ink-500 text-sm" />
          </div>
          Preferences
        </h2>
        <div>
          <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide">Default Expense Split</p>
          <p className="font-semibold text-ink-900 mt-0.5">{family.settings?.defaultSplitRatio || 50}% / {100 - (family.settings?.defaultSplitRatio || 50)}%</p>
        </div>
      </div>

      {/* Notifications — email prefs plus the mobile push prefs, which were
          previously only reachable from the phone. */}
      <NotificationSettings />

      {/* Sits directly after Notifications: it only matters as the fallback
          clock for scheduled reminders, so it reads as a footnote to them. */}
      <FamilyTimezoneSettings />

      {/* Mobile App */}
      <div className="card p-6 mb-6">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-2">
          <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
            <HiDeviceMobile className="text-ink-500 text-sm" />
          </div>
          Mobile App
        </h2>
        <p className="text-sm text-ink-500 mb-4">
          coparent is also on iPhone, iPad and Android — push notifications, biometric
          unlock, and scheduling on the go.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href="https://apps.apple.com/us/app/coparent-co-parenting-hub/id6761960413?utm_source=web&utm_medium=settings"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            <HiDeviceMobile className="text-base" />
            App Store
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.letscoparent.app&utm_source=web&utm_medium=settings"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <HiDeviceMobile className="text-base" />
            Google Play
          </a>
        </div>
      </div>

      {/* Sign out */}
      <div className="card p-6 mb-6">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-2">
          <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
            <HiLogout className="text-ink-500 text-sm" />
          </div>
          Account
        </h2>
        <p className="text-sm text-ink-500 mb-4">
          Sign out of coparent on this device. You can sign back in anytime.
        </p>
        <button
          onClick={handleLogout}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-100 text-ink-700 hover:bg-surface-200 font-semibold text-sm transition-colors"
        >
          <HiLogout className="text-base" />
          Sign Out
        </button>
      </div>

      {/* Danger zone */}
      <div className="card p-6 border border-red-100">
        <h2 className="font-bold text-red-700 flex items-center gap-2.5 text-[0.9375rem] mb-2">
          <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
            <HiTrash className="text-red-500 text-sm" />
          </div>
          Danger zone
        </h2>
        <p className="text-sm text-ink-500 mb-4">
          Permanently delete your account and remove yourself from any shared families.
        </p>
        <Link
          to="/settings/delete-account"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-semibold text-sm transition-colors"
        >
          <HiTrash className="text-base" />
          Delete Account
        </Link>
      </div>
    </div>
  );
};

export default SettingsPage;
